import { Navbar, Footer } from "@/components/Layout";
import { useListingFlow } from "@/contexts/ListingFlowContext";
import { useNavigate } from "react-router-dom";
import { Plus, X, ImageIcon } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { compressImage } from "@/lib/imageCompress";
import { useState, useRef } from "react";
import { toast } from "sonner";

export default function CreateListingStep2() {
  const { data, updateData } = useListingFlow();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  if (!data.category) {
    navigate("/skapa-annons", { replace: true });
    return null;
  }

  const canContinue = data.images.length >= 1 && data.title.trim() !== "" && data.description.trim().length >= 1;

  // Compression moved to shared helper (see src/lib/imageCompress.ts)

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !user) return;

    const remaining = 6 - data.images.length;
    if (remaining <= 0) {
      toast.error("Max 6 bilder tillåtna");
      return;
    }

    setUploading(true);
    const newUrls: string[] = [];
    const filesToUpload = Array.from(files).slice(0, remaining);

    for (const file of filesToUpload) {
      try {
        const { blob, ext, contentType } = await compressImage(file);
        const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage
          .from("listing-images")
          .upload(fileName, blob, { contentType });
        if (error) throw error;
        const { data: urlData } = supabase.storage.from("listing-images").getPublicUrl(fileName);
        newUrls.push(urlData.publicUrl);
      } catch (err: any) {
        toast.error("Kunde inte ladda upp bild: " + err.message);
      }
    }

    updateData({ images: [...data.images, ...newUrls] });
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (index: number) => {
    updateData({ images: data.images.filter((_, i) => i !== index) });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <div className="container max-w-lg flex-1 py-6">
        <BackButton fallback="/skapa-annons" />

        <h1 className="mb-6 font-display text-2xl font-bold text-foreground">Bilder, titel & beskrivning</h1>

        {/* Image upload */}
        <div className="mb-4">
          <Label className="mb-2 block">Bilder (max 6)</Label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || data.images.length >= 6}
            className="flex h-24 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/50 text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50"
          >
            {uploading ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            ) : (
              <>
                <ImageIcon className="h-5 w-5" />
                <span>Lägg till bilder</span>
              </>
            )}
          </button>
        </div>

        {/* Image preview row */}
        {data.images.length > 0 && (
          <div className="mb-6 flex gap-3 overflow-x-auto pb-2 py-[10px] px-0">
            {data.images.map((url, i) => (
              <div key={i} className="relative shrink-0">
                <img
                  src={url}
                  alt={`Uppladdad annonsbild ${i + 1}`}
                  className="h-20 w-20 rounded-lg object-cover py-0"
                />
                <button
                  onClick={() => removeImage(i)}
                  className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Title */}
        <div className="mb-4">
          <Label>Titel på din annons</Label>
          <Input
            value={data.title}
            onChange={(e) => updateData({ title: e.target.value })}
            placeholder="T.ex. Björkved, torkad 1 år"
          />
        </div>

        {/* Description */}
        <div className="mb-6">
          <Label>Beskrivning</Label>
          <Textarea
            value={data.description}
            onChange={(e) => updateData({ description: e.target.value })}
            placeholder="Beskriv vad du säljer..."
            rows={4}
          />
        </div>

        <Button className="w-full" disabled={!canContinue} onClick={() => navigate("/skapa-annons/platsinfo")}>
          Fortsätt
        </Button>
      </div>
      <Footer hideOnMobile />
    </div>
  );
}
